<template>
  <Teleport to="body" :disabled="inline">
    <Transition name="music-panel" @after-leave="releaseAnchor">
      <div v-if="open" class="music-panel-anchor" :class="{ 'opens-above': above, 'keyboard-open': keyboardOpen }" :style="position" @pointerenter="hold" @pointerleave="scheduleClose" @focusout="onFocusOut">
        <section :id="panelId" ref="panel" class="music-panel" role="dialog" aria-label="Music controls" @pointerdown="keyboardOpen = false" @keydown.esc.stop.prevent="close(true)">
          <header class="music-track">
            <div class="music-art" :class="{ 'is-playing': state.playing }" aria-hidden="true">
              <img v-if="state.track.cover && !coverFailed" :src="state.track.cover" alt="" width="52" height="52" @error="coverFailed = true" />
              <div v-else class="music-record"><span></span></div>
            </div>
            <div class="music-track-copy" aria-live="polite" aria-atomic="true">
              <h2>{{ state.track.title }}</h2>
              <div class="music-artist-row">
                <p>{{ state.track.artist || 'Background music' }}</p>
                <span class="music-wave" :class="{ 'is-playing': state.playing && !state.loading }" aria-hidden="true">
                  <span v-for="bar in 5" :key="bar"></span>
                </span>
              </div>
            </div>
          </header>

          <div class="music-transport">
            <button ref="firstControl" type="button" aria-label="Previous track" @click="command('previous')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path class="solid" d="M6 5h2v14H6zm13 1v12L9 12z" /></svg>
            </button>
            <button class="music-play" type="button" :aria-label="state.playing ? 'Pause music' : 'Play music'" :aria-busy="state.loading" @click="command('toggle')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path class="solid" :d="state.playing ? 'M7 5h3v14H7zm7 0h3v14h-3z' : 'm8 5 11 7-11 7z'" /></svg>
            </button>
            <button type="button" aria-label="Next track" @click="command('next')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path class="solid" d="M16 5h2v14h-2zM5 6v12l10-6z" /></svg>
            </button>
          </div>

          <div class="music-progress">
            <input type="range" min="0" :max="state.duration || 1" step="1" :value="scrubTime ?? state.time" :style="{ '--range-fill': progress + '%' }" aria-label="Track position" :aria-valuetext="`${clock(scrubTime ?? state.time)} of ${clock(state.duration)}`" @input="scrubTime = Number($event.target.value)" @change="seek" @pointerdown="dragging = true" @lostpointercapture="finishDrag" @pointercancel="cancelSeek" />
            <div class="music-times"><span>{{ clock(scrubTime ?? state.time) }}</span><span>{{ clock(state.duration) }}</span></div>
          </div>

          <div class="music-volume">
            <button type="button" :aria-label="state.volume === 0 ? 'Unmute music' : 'Mute music'" @click="toggleMute">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m11 5-5 4H3v6h3l5 4z" /><path v-if="state.volume > 0" d="M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14" /><path v-else d="m16 9 5 6m0-6-5 6" /></svg>
            </button>
            <input type="range" min="0" max="100" step="1" :value="Math.round(state.volume * 100)" :style="{ '--range-fill': state.volume * 100 + '%' }" aria-label="Music volume" :aria-valuetext="`${Math.round(state.volume * 100)} percent`" @input="changeVolume" @pointerdown="dragging = true" @lostpointercapture="finishDrag" />
          </div>
          <p v-if="state.error" class="music-error" role="status">{{ state.error }}</p>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'MusicControls',
  props: { inline: Boolean, triggerSelector: { type: String, default: '[data-music-control]' }, panelId: { type: String, default: 'site-music-panel' } },
  data: () => ({
    open: false, above: false, keyboardOpen: false, position: {}, dragging: false,
    scrubTime: null, lastVolume: 1, coverFailed: false,
    state: { track: { title: 'Music', artist: '', cover: '' }, time: 0, duration: 0, playing: false, loading: false, volume: 1, error: '' },
  }),
  computed: {
    progress() { return Math.min(100, (this.scrubTime ?? this.state.time) / (this.state.duration || 1) * 100); },
  },
  watch: {
    '$route.fullPath'() { this.close(); },
  },
  mounted() {
    this._triggers = Array.from(document.querySelectorAll(this.triggerSelector));
    this._onState = event => {
      if (event.detail.track.src !== this.state.track.src) { this.coverFailed = false; this.scrubTime = null; }
      this.state = event.detail;
      if (event.detail.volume > 0) this.lastVolume = event.detail.volume;
    };
    this._onEnter = event => {
      if (event.pointerType !== 'mouse' || !this.canOpen()) return;
      this.hold();
      const anchor = event.currentTarget;
      this._openTimer = setTimeout(() => this.show(anchor), 90);
    };
    this._onFocus = event => {
      if (this._returningFocus) return;
      if (this.canOpen() && event.currentTarget.matches(':focus-visible')) this.show(event.currentTarget, true);
    };
    this._onKey = event => {
      if (!this.canOpen()) return;
      if (event.key === 'ArrowDown' || (this.open && event.key === 'Tab' && !event.shiftKey)) {
        event.preventDefault(); event.stopPropagation();
        this.show(event.currentTarget, true);
        this.$nextTick(() => this.$refs.firstControl?.focus());
      } else if (event.key === 'Escape') this.close();
    };
    this._onOutside = event => {
      if (this.open && !this.$refs.panel?.contains(event.target) && !this._anchor?.contains(event.target)) this.close();
    };
    this._onResize = () => { this.updateAvailability(); if (this.open) this.place(); };
    this._triggers.forEach(trigger => {
      trigger.addEventListener('pointerenter', this._onEnter);
      trigger.addEventListener('pointerleave', this.scheduleClose);
      trigger.addEventListener('focus', this._onFocus);
      trigger.addEventListener('blur', this.onFocusOut);
      trigger.addEventListener('keydown', this._onKey);
    });
    window.addEventListener('site-music-state', this._onState);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('scroll', this.place, { passive: true });
    document.addEventListener('pointerdown', this._onOutside, true);
    this.updateAvailability();
    this.command('request');
  },
  beforeUnmount() {
    this.close();
    this.releaseAnchor();
    this._triggers.forEach(trigger => {
      trigger.removeEventListener('pointerenter', this._onEnter);
      trigger.removeEventListener('pointerleave', this.scheduleClose);
      trigger.removeEventListener('focus', this._onFocus);
      trigger.removeEventListener('blur', this.onFocusOut);
      trigger.removeEventListener('keydown', this._onKey);
    });
    window.removeEventListener('site-music-state', this._onState);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this.place);
    document.removeEventListener('pointerdown', this._onOutside, true);
  },
  methods: {
    canOpen() { return window.matchMedia('(min-width: 761px) and (hover: hover) and (pointer: fine)').matches; },
    updateAvailability() {
      const enabled = this.canOpen();
      this._triggers.forEach(trigger => {
        if (enabled) trigger.setAttribute('aria-haspopup', 'dialog');
        else { trigger.removeAttribute('aria-haspopup'); trigger.removeAttribute('aria-expanded'); }
      });
      if (!enabled) this.close();
    },
    show(anchor, keyboard = false) {
      if (!anchor || !this.canOpen()) return;
      this.hold();
      if (this._anchor && this._anchor !== anchor) {
        this._anchor.removeAttribute('aria-controls');
        this._anchor.setAttribute('aria-expanded', 'false');
        this._anchor.classList.remove('music-controls-open');
      }
      this._anchor = anchor;
      anchor.setAttribute('aria-expanded', 'true');
      anchor.setAttribute('aria-controls', this.panelId);
      anchor.classList.add('music-controls-open');
      this.keyboardOpen = keyboard;
      this.open = true;
      this.command('request');
      this.place();
      this.$nextTick(this.place);
    },
    place() {
      if (!this.open || !this._anchor) return;
      const box = this._anchor.getBoundingClientRect();
      if (!box.width || box.bottom < 0 || box.top > innerHeight) { this.close(); return; }
      const height = (this.$refs.panel?.offsetHeight || 238) + 14;
      const left = Math.max(12, Math.min(innerWidth - 308, box.right - 296));
      this.above = box.bottom + height > innerHeight - 12 && box.top > height;
      this.position = { left: `${left}px`, top: `${this.above ? box.top - height : box.bottom}px`, '--music-tail': `${Math.max(20, Math.min(276, box.left + box.width / 2 - left))}px` };
    },
    hold() { clearTimeout(this._closeTimer); clearTimeout(this._openTimer); },
    scheduleClose() {
      this.hold();
      this._closeTimer = setTimeout(() => {
        if (this.$refs.panel?.matches(':hover') || this._anchor?.matches(':hover')) return;
        if (!this.dragging && !(this.keyboardOpen && this.$refs.panel?.contains(document.activeElement))) this.close();
      }, 180);
    },
    onFocusOut() {
      this.$nextTick(() => {
        if (!this.$refs.panel?.contains(document.activeElement) && document.activeElement !== this._anchor) this.scheduleClose();
      });
    },
    close(returnFocus = false) {
      this.hold();
      const scene = this.inline && this.$refs.panel?.closest('dialog')?.querySelector('[data-exhibition-keyboard-focus]');
      if (!returnFocus && scene && this.$refs.panel?.contains(document.activeElement)) scene.focus({ preventScroll: true });
      this.open = false;
      this.dragging = false;
      this.scrubTime = null;
      this._anchor?.setAttribute('aria-expanded', 'false');
      this._anchor?.classList.remove('music-controls-open');
      if (returnFocus && this._anchor?.isConnected) {
        this._returningFocus = true;
        this._anchor.focus({ preventScroll: true });
        this._returningFocus = false;
      }
    },
    releaseAnchor() { if (!this.open) { this._anchor?.removeAttribute('aria-controls'); this._anchor = null; } },
    command(action, values = {}) {
      window.dispatchEvent(new CustomEvent(action === 'request' ? 'site-music-request' : 'site-music-command', { detail: { action, ...values } }));
    },
    clock(seconds) { const time = Math.max(0, Math.floor(seconds || 0)); return `${Math.floor(time / 60)}:${String(time % 60).padStart(2, '0')}`; },
    seek(event) { this.command('seek', { time: Number(event.target.value) }); this.scrubTime = null; this.dragging = false; },
    cancelSeek() { this.scrubTime = null; this.finishDrag(); },
    finishDrag() { this.dragging = false; if (!this.$refs.panel?.matches(':hover')) this.scheduleClose(); },
    changeVolume(event) { this.command('volume', { volume: Number(event.target.value) / 100 }); },
    toggleMute() { this.command('volume', { volume: this.state.volume === 0 ? this.lastVolume : 0 }); },
  },
};
</script>

<style scoped>
.music-panel-anchor { position: fixed; z-index: 1100; width: 296px; padding-top: 14px; box-sizing: border-box; transform-origin: var(--music-tail) top; }
.music-panel { --site-bg: #fff; --site-text-primary: #333; --site-text-secondary: #77746f; }
.music-panel { position: relative; box-sizing: border-box; padding: 20px; border: 1px solid color-mix(in srgb, var(--site-text-primary) 11%, transparent); border-radius: 14px; background: color-mix(in srgb, var(--site-bg, #fff) 96%, transparent); color: var(--site-text-primary, #333); box-shadow: 0 12px 36px #00000012, 0 2px 8px #00000006; backdrop-filter: blur(18px); font: 13px/1.4 -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif; cursor: var(--site-default-cursor); }
.music-panel::before { content: ''; position: absolute; top: -5px; left: calc(var(--music-tail) - 5px); width: 8px; height: 8px; background: var(--site-bg, #fff); border: inherit; border-right: 0; border-bottom: 0; transform: rotate(45deg); }
.music-panel-anchor.opens-above { padding-top: 0; padding-bottom: 14px; transform-origin: var(--music-tail) bottom; }
.opens-above .music-panel::before { top: auto; bottom: -5px; transform: rotate(225deg); }
.music-track { display: flex; align-items: center; gap: 13px; }
.music-art { display: grid; place-items: center; width: 52px; height: 52px; flex-shrink: 0; overflow: hidden; border-radius: 7px; }
.music-art img { display: block; width: 100%; height: 100%; border-radius: 0; object-fit: cover; }
.music-record { display: grid; place-items: center; width: 42px; height: 42px; border-radius: 50%; background: repeating-radial-gradient(circle at center, #333 0 1px, #272727 2px 3px); box-shadow: 0 2px 4px #0002; }
.music-record span { width: 12px; height: 12px; border: 4px solid #baa381; box-sizing: border-box; border-radius: 50%; background: #eee9e1; }
.music-track-copy { min-width: 0; flex: 1; }
.music-track h2 { margin: 0; color: inherit; font: 550 15px/1.35 -apple-system, BlinkMacSystemFont, system-ui, sans-serif; letter-spacing: -.015em; }
.music-artist-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 4px; }
.music-track p { margin: 0; color: var(--site-text-secondary, #777); font: inherit; font-size: 12px; }
.music-wave { display: flex; align-items: center; gap: 2.5px; height: 16px; flex-shrink: 0; color: var(--site-text-secondary); opacity: .5; transition: opacity 180ms ease; }
.music-wave.is-playing { opacity: .85; }
.music-wave span { width: 1.5px; height: 12px; border-radius: 2px; background: currentColor; animation: music-wave 1600ms cubic-bezier(.45,0,.55,1) infinite; animation-play-state: paused; }
.music-wave span:nth-child(1) { height: 7px; animation-delay: -700ms; }
.music-wave span:nth-child(2) { height: 12px; animation-duration: 1900ms; animation-delay: -1100ms; }
.music-wave span:nth-child(3) { height: 16px; animation-duration: 1700ms; animation-delay: -350ms; }
.music-wave span:nth-child(4) { height: 11px; animation-duration: 2100ms; animation-delay: -1300ms; }
.music-wave span:nth-child(5) { height: 6px; animation-duration: 1800ms; animation-delay: -600ms; }
.music-wave.is-playing span { animation-play-state: running; }
@keyframes music-wave { 0%, 100% { transform: scaleY(.4); } 40% { transform: scaleY(1); } 70% { transform: scaleY(.6); } }
.music-transport { display: flex; justify-content: center; align-items: center; gap: 19px; margin: 17px 0 7px; }
.music-panel button { appearance: none; display: grid; place-items: center; flex-shrink: 0; width: 32px; height: 32px; padding: 7px; border: 0; border-radius: 50%; background: transparent; color: inherit; cursor: var(--site-interactive-cursor); transition: background-color 140ms ease, color 140ms ease, transform 140ms ease; }
.music-panel button:active { transform: scale(.94); }
.music-panel button:focus-visible, .music-panel input:focus-visible { outline: 2px solid var(--site-accent); outline-offset: 3px; }
.music-panel svg { display: block; width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.35; stroke-linejoin: round; stroke-linecap: round; }
.music-panel .solid { fill: currentColor; stroke: none; }
.music-panel .music-play { width: 38px; height: 38px; background: var(--site-text-primary, #333); color: var(--site-bg, #fff); }
.music-play svg { width: 20px; height: 20px; }
.music-panel input[type='range'] { appearance: none; -webkit-appearance: none; display: block; width: 100%; height: 18px; margin: 0; padding: 0; border: 0; border-radius: 0; background: transparent; cursor: var(--site-interactive-cursor); accent-color: var(--site-text-primary); }
.music-panel input::-webkit-slider-runnable-track { height: 3px; border-radius: 3px; background: linear-gradient(to right, var(--site-text-primary) var(--range-fill), color-mix(in srgb, var(--site-text-primary) 14%, transparent) var(--range-fill)); }
.music-panel input::-moz-range-track { height: 3px; border-radius: 3px; background: linear-gradient(to right, var(--site-text-primary) var(--range-fill), color-mix(in srgb, var(--site-text-primary) 14%, transparent) var(--range-fill)); }
.music-panel input::-webkit-slider-thumb { appearance: none; -webkit-appearance: none; width: 9px; height: 9px; margin-top: -3px; border: 0; border-radius: 50%; background: var(--site-text-primary); }
.music-panel input::-moz-range-thumb { width: 9px; height: 9px; border: 0; border-radius: 50%; background: var(--site-text-primary); }
.music-times { display: flex; justify-content: space-between; margin-top: 1px; color: var(--site-text-secondary); font-size: 10px; font-variant-numeric: tabular-nums; }
.music-volume { display: flex; align-items: center; gap: 7px; margin: 11px -5px -7px; }
.music-volume button { width: 28px; height: 28px; padding: 5px; }
.music-volume input { flex: 1; min-width: 0; }
.music-volume svg { width: 15px; height: 15px; }
.music-panel .music-error { margin: 15px 0 0; color: var(--site-text-secondary); font: inherit; font-size: 12px; }
.music-panel-enter-active { transition: opacity 180ms ease, transform 200ms cubic-bezier(.23,1,.32,1); }
.music-panel-leave-active { transition: opacity 120ms ease, transform 140ms cubic-bezier(.23,1,.32,1); pointer-events: none; }
/* Keep fine bars and type at their final width throughout the reveal. */
.music-panel-enter-from, .music-panel-leave-to { opacity: 0; transform: translateY(-6px); }
.opens-above.music-panel-enter-from, .opens-above.music-panel-leave-to { transform: translateY(6px); }
.keyboard-open.music-panel-enter-active, .keyboard-open.music-panel-leave-active { transition: none; }
:global(body.dark-mode .music-panel) { --site-bg: #242425; --site-text-primary: #e6e2dc; --site-text-secondary: #b4b1ad; box-shadow: 0 12px 36px #0004; }
@media (hover: hover) and (pointer: fine) {
  .music-panel button:hover { background-color: color-mix(in srgb, var(--site-text-primary) 7%, transparent); }
  .music-panel .music-play:hover { background-color: color-mix(in srgb, var(--site-text-primary) 82%, var(--site-bg)); }
}
@media (max-width: 760px), (hover: none) { .music-panel-anchor { display: none; } }
@media (prefers-reduced-motion: reduce) {
  .music-wave span { animation-play-state: paused !important; }
  .music-panel-enter-active, .music-panel-leave-active, .music-panel button { transition: none; }
  .music-panel-enter-from, .music-panel-leave-to { transform: none; }
}
</style>
